import {
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { useQuery } from "@tanstack/react-query";
import dayjs, { type Dayjs } from "dayjs";
import { useMemo, useState, type FC } from "react";
import { useOrganization } from "../../../contexts/OrganizationProvider";
import {
  fetchStatisticsDailyTrends,
  fetchStatisticsHourlyTrends,
} from "../statisticsDailyTrendsApi";
import {
  buildStatisticsQueryFilters,
  type StatisticsFilters,
} from "../types";
import {
  StatisticsDailyLineChart,
  type DailyChartRow,
} from "./StatisticsDailyLineChart";

interface StatisticsDailyTrendsSectionProps {
  filters: StatisticsFilters;
}

type TrendsMode = "daily" | "hourly";

function defaultFrom(): Dayjs {
  return dayjs().subtract(9, "day").startOf("day");
}

function defaultTo(): Dayjs {
  return dayjs().startOf("day");
}

function defaultHourlyDate(): Dayjs {
  return dayjs().startOf("day");
}

const chartCardSx = {
  flex: { xs: "0 0 auto", md: "1 1 0" },
  minWidth: 0,
  p: 2,
  borderRadius: 2,
  border: "1px solid",
  borderColor: "divider",
} as const;

export const StatisticsDailyTrendsSection: FC<
  StatisticsDailyTrendsSectionProps
> = ({ filters }) => {
  const { currOrganization } = useOrganization();
  const [mode, setMode] = useState<TrendsMode>("daily");
  const [from, setFrom] = useState<Dayjs>(defaultFrom);
  const [to, setTo] = useState<Dayjs>(defaultTo);
  const [hourlyDate, setHourlyDate] = useState<Dayjs>(defaultHourlyDate);

  const { dateFrom, dateTo } = useMemo(() => {
    let a = from.startOf("day");
    let b = to.startOf("day");
    if (a.isAfter(b)) {
      const t = a;
      a = b;
      b = t;
    }
    return {
      dateFrom: a.format("YYYY-MM-DD"),
      dateTo: b.format("YYYY-MM-DD"),
    };
  }, [from, to]);

  const hourlyDateStr = useMemo(
    () => hourlyDate.startOf("day").format("YYYY-MM-DD"),
    [hourlyDate],
  );

  const dailyQuery = useQuery({
    queryKey: [
      "statistics-daily-trends",
      currOrganization?.id,
      filters,
      dateFrom,
      dateTo,
    ],
    queryFn: () =>
      fetchStatisticsDailyTrends({
        ...buildStatisticsQueryFilters(currOrganization!.id, filters),
        dateFrom,
        dateTo,
      }),
    enabled: !!currOrganization?.id && mode === "daily",
    staleTime: 1000 * 60 * 2,
  });

  const hourlyQuery = useQuery({
    queryKey: [
      "statistics-hourly-trends",
      currOrganization?.id,
      filters,
      hourlyDateStr,
    ],
    queryFn: () =>
      fetchStatisticsHourlyTrends({
        ...buildStatisticsQueryFilters(currOrganization!.id, filters),
        date: hourlyDateStr,
      }),
    enabled: !!currOrganization?.id && mode === "hourly",
    staleTime: 1000 * 60 * 2,
  });

  const isLoading =
    mode === "daily" ? dailyQuery.isLoading : hourlyQuery.isLoading;

  const { regChartData, openedChartData, regTotal, openedTotal } = useMemo(() => {
    const empty: DailyChartRow[] = [];
    if (mode === "daily") {
      const days = dailyQuery.data?.days;
      if (!days?.length) {
        return {
          regChartData: empty,
          openedChartData: empty,
          regTotal: 0,
          openedTotal: 0,
        };
      }
      const reg: DailyChartRow[] = [];
      const opened: DailyChartRow[] = [];
      let regSum = 0;
      let openedSum = 0;
      for (const row of days) {
        const label = dayjs(row.date).format("DD/MM");
        const rv = row.volunteerRegistrations;
        const ov = row.openedMissions;
        regSum += rv;
        openedSum += ov;
        reg.push({ dayLabel: label, value: rv });
        opened.push({ dayLabel: label, value: ov });
      }
      return {
        regChartData: reg,
        openedChartData: opened,
        regTotal: regSum,
        openedTotal: openedSum,
      };
    }

    const hours = hourlyQuery.data?.hours;
    if (!hours?.length) {
      return {
        regChartData: empty,
        openedChartData: empty,
        regTotal: 0,
        openedTotal: 0,
      };
    }
    const reg: DailyChartRow[] = [];
    const opened: DailyChartRow[] = [];
    let regSum = 0;
    let openedSum = 0;
    for (const row of hours) {
      regSum += row.volunteerRegistrations;
      openedSum += row.openedMissions;
      reg.push({ dayLabel: row.hourLabel, value: row.volunteerRegistrations });
      opened.push({ dayLabel: row.hourLabel, value: row.openedMissions });
    }
    return {
      regChartData: reg,
      openedChartData: opened,
      regTotal: regSum,
      openedTotal: openedSum,
    };
  }, [mode, dailyQuery.data?.days, hourlyQuery.data?.hours]);

  const regTitle =
    mode === "daily" ? "מאצ׳ים (יומי)" : "מאצ׳ים (שעתי, יום מלא)";
  const openedTitle =
    mode === "daily"
      ? "משימות + בקשות סיוע (יומי)"
      : "משימות + בקשות סיוע (שעתי, יום מלא)";

  return (
    <Stack spacing={2} sx={{ width: "100%" }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
      >
        <Typography variant="h6" fontWeight={600} color="primary">
          מגמות יומיות
        </Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          color="primary"
          value={mode}
          onChange={(_, v: TrendsMode | null) => v && setMode(v)}
          aria-label="תצוגה"
        >
          <ToggleButton value="daily">יומי</ToggleButton>
          <ToggleButton value="hourly">שעתי</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {mode === "daily" ? (
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ sm: "center" }}
          flexWrap="wrap"
        >
          <DatePicker
            label="מתאריך"
            value={from}
            onChange={(v) => v && setFrom(v.startOf("day"))}
            maxDate={dayjs()}
            slotProps={{
              textField: { size: "small" },
              popper: { className: "date-picker-dialog" },
              layout: { className: "date-field-ltr" },
            }}
          />
          <DatePicker
            label="עד תאריך"
            value={to}
            onChange={(v) => v && setTo(v.startOf("day"))}
            maxDate={dayjs()}
            slotProps={{
              textField: { size: "small" },
              popper: { className: "date-picker-dialog" },
              layout: { className: "date-field-ltr" },
            }}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ maxWidth: 360 }}
          >
            מאצ'ים לפי גודל קבוצה (בזמן אמת) + מאצ'ים ידניים כלליים. ברירת מחדל:
            10 הימים האחרונים. עד 120 ימים בבקשה אחת.
          </Typography>
        </Stack>
      ) : (
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ sm: "center" }}
          flexWrap="wrap"
        >
          <DatePicker
            label="תאריך"
            value={hourlyDate}
            onChange={(v) => v && setHourlyDate(v.startOf("day"))}
            maxDate={dayjs()}
            slotProps={{
              textField: { size: "small" },
              popper: { className: "date-picker-dialog" },
              layout: { className: "date-field-ltr" },
            }}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ maxWidth: 360 }}
          >
            תצוגה לפי כל שעות היממה (00:00–23:00, שעון ישראל). מאצ'ים לפי גודל
            קבוצה + ידני כללי.
          </Typography>
        </Stack>
      )}

      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems="stretch"
      >
        <Paper elevation={1} sx={chartCardSx}>
          <StatisticsDailyLineChart
            title={regTitle}
            color="#065bf9"
            data={regChartData}
            isLoading={isLoading}
            xUnit={mode === "daily" ? "יום" : "שעה"}
            rangeTotal={regTotal}
          />
        </Paper>
        <Paper elevation={1} sx={chartCardSx}>
          <StatisticsDailyLineChart
            title={openedTitle}
            color="#0d7c66"
            data={openedChartData}
            isLoading={isLoading}
            xUnit={mode === "daily" ? "יום" : "שעה"}
            rangeTotal={openedTotal}
          />
        </Paper>
      </Stack>
    </Stack>
  );
};
